/* eslint-disable react/prop-types */
import React, {
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ApolloProvider } from "@apollo/client";
import dynamic from "next/dynamic";

import { Root } from "@aqar-app/react-ui";
import { Rubik } from "next/font/google";
import { useApollo } from "../lib/apolloClient";
import ga4 from "@/lib/tracking/ga4Core";
import { FavListingsProvider } from "../contexts/fav-listings/favListings.provider";
import { AppContextProvider } from "../contexts/app/app.provider";
import AppLayout from "../containers/layouts/AppLayout";
import { getClientCookie, setLoginCookies } from "../lib/cookiesHelpers";
import ErrorBoundary from "../containers/ErrorBoundary";
import "../styles/applePay.css";
// eslint-disable-next-line import/no-extraneous-dependencies
import "../styles/globals.css";
import "@aqar-app/react-ui/dist/index.css";
import "@aqar-app/aqar-design-system/lib/index.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import Config from "../common/config";
import PathContextProvider from "../contexts/path/path";
import AuthContextProvider, {
  AuthContext,
} from "../contexts/auth/auth.provider";
import { SSRError } from "@/components/error/ssr";
import { Intercom, INTERCOM_EVENT } from "@/components/intercom";
import dayjs from "dayjs";
import "dayjs/locale/ar"; // use loaded locale globally
import relativeTime from "dayjs/plugin/relativeTime";
import { getDeviceID } from "@/lib/helpers";
import { useCurrencyReplacer } from "@/hooks/useCurrencyReplacer";
import UserLikesLoader from "@/components/userLikesLoader/userLikesLoader";

dayjs.locale("ar");
dayjs.extend(relativeTime);
const localStorageVersion = "1.0";
const font = Rubik({
  subsets: ["arabic"],
  weight: ["300", "400", "500", "600"],
  display: "fallback",
  preload: true,
});
const Modal = dynamic(() => import("../components/modal/Modal"), {
  ssr: false,
});
// eslint-disable-next-line react/display-name
const GlobalModal = React.forwardRef((props, ref) => {
  const [modalProps, setModalProps] = useState({});
  useImperativeHandle(ref, () => ({
    setModalProps,
  }));
  return <Modal {...modalProps} />;
});

function UserAboutLoader() {
  const { user, updateContext } = useContext(AuthContext);
  const doneSync = useRef(false);
  useEffect(() => {
    if (!user) return;
    const localAbout = localStorage.getItem("userAbout");
    if (user.about && !localAbout) {
      return localStorage.setItem("userAbout", user.about);
    }

    if (user.about && localAbout !== user.about) {
      return localStorage.setItem("userAbout", user.about);
    }

    if (user.about === localAbout) {
      return;
    }

    if (doneSync.current) return;
    updateContext({
      user: {
        ...user,
        about: localAbout,
      },
    });

    doneSync.current = true;
  }, [user]);
  return null;
}
function AqarApp({ Component, pageProps: { error, ...pageProps }, router }) {
  const Layout = Component.getLayout ?? AppLayout;
  useCurrencyReplacer();
  useEffect(function migrateDeviveTokenToCookie() {
    let deviceToken = getDeviceID();
    if (!deviceToken) return;
    // set a client cookie using javascript non expired
    document.cookie = `req-device-token=${deviceToken}; path=/;  max-age=3786901200000`;
  }, []);
  useEffect(function localStorageMigrations() {
    const token = localStorage.getItem("webapp_token");
    const deviceToken = localStorage.getItem("req-device-token");
    const version = localStorage.getItem("version");
    if (
      localStorageVersion === version &&
      deviceToken &&
      deviceToken !== "null"
    )
      return;
    localStorage.clear();

    if (token) localStorage.setItem("webapp_token", token);
    localStorage.setItem(
      "req-device-token",
      !deviceToken || deviceToken === "null" ? getDeviceID() : deviceToken,
    );
    localStorage.setItem("version", localStorageVersion);
  }, []);
  const isSSR = typeof router.isSsr === "undefined" || router.isSsr;
  const globalModalRef = useRef(null);
  const handleError = useCallback(
    (
      props = {
        children: <div>انتهت مدة الجلسة، الرجاء إعادة تسجيل الدخول.</div>,
        onOk: () => {
          setLoginCookies("", {});
          window.location.href = `/login?returnTo=${encodeURIComponent(
            window.location.pathname,
          )}`;
        },
        onCancel: () => {
          setLoginCookies("", {});
          window.location.reload();
        },
      },
    ) => {
      const errorProps = {
        visible: true,
        okLabel: "تسجيل الدخول",
        closeOnClickOutside: false,
        closeBtn: true,
        headerIcon: "Warning-colored",
        ...props,
      };
      globalModalRef.current?.setModalProps(errorProps);
    },
    [],
  );

  const apolloClient = useApollo(pageProps, handleError);

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "/@aqar-app-react-ui.css";
    const link2 = document.createElement("link");
    link2.rel = "stylesheet";
    link2.href = "/@aqar-app-ds.css";
    // document.head.prepend(link);
    //document.head.prepend(link2);
    document.body.classList.add(font.className);
  }, []);
  useEffect(() => {
    if (location?.href?.includes("console.log=true")) {
      window.debug = true;
    }
  }, [pageProps]);

  useEffect(() => {
    const gtmContainerId = Config.GA4_TRACKING_ID;
    // const gaPageLevelMetadata = buildPageLevelMetadata(pageProps);
    ga4.initialize(gtmContainerId, undefined);

    const id = setTimeout(() => {
      document.body.dispatchEvent(
        new CustomEvent(INTERCOM_EVENT, {
          bubbles: true,
          detail: { type: "load" },
        }),
      );
      const shouldShowIntercom = location?.href?.includes("int-support=true");
      if (shouldShowIntercom) {
        document.body.dispatchEvent(
          new CustomEvent(INTERCOM_EVENT, {
            bubbles: true,
            detail: { type: "show" },
          }),
        );
      }
    }, 20 * 1000);

    return () => clearTimeout(id);
  }, []);

  // useEffect(() => {
  //   const initialDataLayerData = window.dataLayer?.[1] || {};
  //   const newData = buildPageLevelMetadata(pageProps);
  //   console.log('initialDataLayerData', initialDataLayerData);
  //   console.log('newData', newData);
  //
  //   if (newData?.login_status !== initialDataLayerData?.login_status) {
  //     ga4.pushEvent(newData)
  //   }
  // }, [pageProps]);

  return (
    <AppContextProvider value={pageProps.appState}>
      <ApolloProvider client={apolloClient}>
        <PathContextProvider value={pageProps.path}>
          <AuthContextProvider value={pageProps.authState}>
            <FavListingsProvider>
              <UserLikesLoader />
              <main className={font.className}>
                <Layout {...pageProps} {...pageProps.appState}>
                  <GlobalModal ref={globalModalRef} />

                  {isSSR && error ? (
                    <SSRError error={error} {...pageProps} />
                  ) : (
                    <ErrorBoundary>
                      <Component {...pageProps} />
                      <Root />
                    </ErrorBoundary>
                  )}
                </Layout>
                <Intercom />
                <UserAboutLoader />
              </main>
            </FavListingsProvider>
          </AuthContextProvider>
        </PathContextProvider>
      </ApolloProvider>
    </AppContextProvider>
  );
}

export default AqarApp;
